import { faTrashCan, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, Typography, makeStyles } from '@material-ui/core';
import { ButtonText, DefaultCard, FormHookResult, colors } from 'design-system';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  makeDropDown,
  makePercentageField,
  makeTextField,
  makeTextIdField,
} from 'src/components/MakeFormFields';

import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { AnyAction } from 'redux';
import { list as naturalPersonIdTypes } from 'src/constants/idTypes';
import relationships from 'src/constants/relationships';
import { REQUEST_SEARCH_VALIDATE_PERSON } from 'src/store/person/requestIdentifiers';

const useStyles = makeStyles({
  text: {
    fontSize: 17,
    fontWeight: 400,
    color: colors.madison,
    marginTop: 5,
  },
  container: {
    marginTop: 20,
  },
  buttonText: {
    borderRadius: '80px',
    cursor: 'pointer',
    padding: '5px 10px',
    marginTop: '-7px',
    color: colors.bright_turquoise,
    '&:hover': {
      backgroundColor: colors.gallery,
    },
  },
  delete: {
    color: colors.bright_turquoise,
    fontSize: 16,
    fontWeight: 700,
    marginLeft: '10px',
  },
});

export const BeneficiaryInputs = ({
  getInputProps,
  index,
  showPercentageError,
  totalBeneficiaries,
  onDeleteBeneficiary,
  setCurrentForm,
  currentForm,
  errors,
  setErrors,
  setLoading,
  validatePersonAction,
}: Props): JSX.Element => {
  const classes = useStyles();
  const requestingPerson = useNetworkObserver(REQUEST_SEARCH_VALIDATE_PERSON);
  const [collapse, setCollapse] = useState(false);
  const [refetch, setRefetch] = useState(true);
  const firstName = getInputProps(`beneficiary${index}firstName`)
    .value as string;
  const lastName = getInputProps(`beneficiary${index}lastName`).value as string;
  const fullname = `${firstName} ${lastName}`.trim();

  const handleClickDelete = (): void => {
    if (totalBeneficiaries > 1) {
      onDeleteBeneficiary(index);
    }
  };

  const handleSetErrors = (): void => {
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = {
        error: false,
        errorText: '',
      };
      return newErrors;
    });
  };

  return (
    <DefaultCard
      data-testid="beneficiary-inputs-card"
      title={
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ minWidth: 42 }}>
            <FontAwesomeIcon icon={faUser} fontSize={24} color="#0D2E68" />
          </Box>
          <Typography>Beneficiario # {index + 1}</Typography>
        </Box>
      }
      nonCollapsibleElement={
        collapse ? <span className={classes.text}>{fullname}</span> : undefined
      }
      collapsible
      forceCollapsed={collapse}
      onForceCollapsedUpdate={(): void => setCollapse(!collapse)}
      arrowColor={colors.bright_turquoise}
    >
      <Grid item container xs={12} spacing={3} className={classes.container}>
        <Grid item xs={12} md={6}>
          {makeDropDown({
            inputId: `swi-beneficiary-beneficiary${index}idType`,
            inputName: `beneficiary${index}idType`,
            label: 'Tipo de identificación',
            getInputProps,
            dropdownList: naturalPersonIdTypes,
          })}
        </Grid>

        <Grid key={`swi-beneficiary-beneficiary${index}id`} item xs={12} md={6}>
          {makeTextIdField({
            inputId: `swi-beneficiary-beneficiary${index}id`,
            inputName: `beneficiary${index}id`,
            label: 'Número de identificación',
            getInputProps: () => {
              return {
                ...getInputProps(`beneficiary${index}id`),
                error:
                  errors[index].error ||
                  getInputProps(`beneficiary${index}id`).error,
                errorText:
                  errors[index].errorText ||
                  getInputProps(`beneficiary${index}id`).errorText,
                onChange(newValue) {
                  handleSetErrors();
                  getInputProps(`beneficiary${index}id`).onChange(newValue);
                  setRefetch(true);
                  setLoading(true);
                },
              };
            },
            onBlur: (): void => {
              const idNumber = getInputProps(`beneficiary${index}id`)
                .value as string;
              const idType = getInputProps(`beneficiary${index}idType`)
                .value as string;
              setCurrentForm(index);
              if (refetch) validatePersonAction(idNumber, idType);
              setRefetch(false);
            },
            isLoading:
              requestingPerson === 'requesting' && currentForm === index,
            helperText:
              (getInputProps(`beneficiary${index}idType`).value as string) ===
              'U'
                ? 'Debe iniciar con 1 y contener 12 caracteres'
                : 'Sin espacios ni guiones (ej. 888888888)',
          })}
        </Grid>

        <Grid item xs={12} md={6}>
          {makeTextField({
            inputId: `swi-beneficiary-beneficiary${index}firstName`,
            inputName: `beneficiary${index}firstName`,
            label: 'Nombres',
            getInputProps,
          })}
        </Grid>

        <Grid item xs={12} md={6}>
          {makeTextField({
            inputId: `swi-beneficiary-beneficiary${index}lastName`,
            inputName: `beneficiary${index}lastName`,
            label: 'Apellidos',
            getInputProps,
          })}
        </Grid>

        <Grid item xs={12} md={6}>
          {makeDropDown({
            inputId: `swi-beneficiary-beneficiary${index}relationship`,
            inputName: `beneficiary${index}relationship`,
            label: 'Parentesco',
            getInputProps,
            dropdownList: relationships,
          })}
        </Grid>

        <Grid item xs={12} md={6}>
          {makePercentageField({
            inputId: `swi-beneficiary-beneficiary${index}participationRate`,
            inputName: `beneficiary${index}participationRate`,
            label: 'Porcentaje a asignar',
            getInputProps,
            showError: showPercentageError,
            errorMessage: showPercentageError
              ? 'El porcentaje supera el total del 100% disponible.'
              : '',
          })}
        </Grid>

        <Grid item xs={12} md={6} container justifyContent="flex-start">
          <Box className={classes.buttonText}>
            <ButtonText
              id="sales-web-inspection-delete-beneficiary"
              data-testid="delete-button"
              onClick={handleClickDelete}
            >
              <FontAwesomeIcon
                icon={faTrashCan}
                fontSize={24}
                color={colors.bright_turquoise}
              />{' '}
              <span className={classes.delete}>ELIMINAR</span>
            </ButtonText>
          </Box>
        </Grid>
      </Grid>
    </DefaultCard>
  );
};

interface Props {
  getInputProps: FormHookResult;
  index: number;
  showPercentageError: boolean;
  totalBeneficiaries: number;
  onDeleteBeneficiary: (index: number) => void;
  setCurrentForm: (index: number) => void;
  currentForm: number;
  errors: { error: boolean; errorText: string }[];
  setErrors: Dispatch<
    SetStateAction<
      {
        error: boolean;
        errorText: string;
      }[]
    >
  >;
  validatePersonAction: (
    idNumber: string,
    idType: string,
  ) => Promise<AnyAction>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

export default BeneficiaryInputs;
